import { type OfferSchema, type OffersForPriceSchema, type OffersForSaleSchema } from '@b2ag/types-api-schemas'
import { type EnhancedLogisticOfferForSale } from '@invivodf/module-logistic-offer-client-sdk'

import { type Offer, type OfferBase, type PaymentDueDate, variantId } from '@b2ag/offers/src/offer'
import { type Quota } from '@b2ag/quota/src/quota'
import { type OfferForQuota } from '@b2ag/quota/src/quota-config.business'
import { mapLogisticOffers } from '@b2ag/offers/src/mappers/logisticOffer.mapper'
import {
  type ProduitB2AgWithoutProductsInPackApiResponse,
  type ProduitCatalogueCooperativeB2AgApiResponse,
  ProduitType,
  type VariantCatalogueCooperativeB2AgApiResponse,
} from '@b2ag/module-product-sdk'
import { type CooperativeProduct, type CooperativeVariant, type FlattenedPackProduct, type Product } from './product'
import { type Profile } from '../domain/product'

export const defaultShippingWays = {
  pickup_point: {
    title: 'Livraison relais coopérative',
    kind: 'pickup_point',
  },
  farm: {
    title: 'Livraison à la ferme',
    kind: 'farm',
  },
  click_and_collect: {
    title: 'Retrait en magasin',
    kind: 'click_and_collect',
  },
}

function mapShippingWayFromApi(shippingWay: string): { kind: string; title: string } {
  return defaultShippingWays[shippingWay as keyof typeof defaultShippingWays] || {}
}

export const mapOfferFromOffersForPriceApi = (
  offer: OffersForPriceSchema[0] = {} as OffersForPriceSchema[0],
): OfferBase => {
  const [, variantInvivoId, reference] = offer.offer_id.split(':') as [string, string, string]

  return {
    offerId: offer.offer_id,
    sellerCustomId: reference,
    priceType: offer.price_type,
    measurementPrice: offer.measurement_price,
    unitPrice: offer.unit_price,
    rpdUnit: offer.tax_rpd_unit,
    tvaUnitPrice: offer.tax_tva_unit_price,
    rpdUnitPrice: offer.tax_rpd_unit_price,
    [variantId]: variantInvivoId,
    productId: offer.product_id,
    isSoldAsUnit: offer.is_sold_as_unit || false,
    quantityMax: offer.quantity_max,
    discountId: undefined,
    offerType: offer.type || '',
    allowedWithoutStock: offer.allowed_without_stock,
    stockManagementType: offer.variant_stock_management_type,
    measureQuantity: offer.variant_measure_quantity,
    measureUnit: offer.variant_measure_unit,
    quota: offer.quota as Quota,
  }
}

export const mapOfferFromOffersForSaleApi = (offer: OffersForSaleSchema[0] = {} as OffersForSaleSchema[0]): Offer => {
  const logisticOffers: EnhancedLogisticOfferForSale[] = mapLogisticOffers(offer)
  return Object.assign(mapOfferFromOffersForPriceApi(offer), {
    shippingStartDate: offer.shipping_start_date,
    shippingEndDate: offer.shipping_end_date,
    validityEndDate: offer.validity_end_date ? new Date(offer.validity_end_date) : undefined,
    taxTvaPercent: offer.tax_tva_percent,
    logisticOffers,
    quantityMin: offer.quantity_min,
    quantityStep: offer.quantity_step,
    intervalName: offer.interval_name,
    isTechExclusive: offer.is_tech_exclusive,
    description: offer.description,
    productIsRestrictedExplosivesPrecursor: !!offer.product_is_restricted_explosives_precursor,
    paymentDueDateList: offer.payment_due_date_list as PaymentDueDate[],
    salesUnit: offer.sales_unit || null,
  })
}

type ExtendCooperativeVariantSchema = VariantCatalogueCooperativeB2AgApiResponse

const mapVariantFromApi = (
  variant: ExtendCooperativeVariantSchema,
  productKind: string,
  productId: string,
): CooperativeVariant => {
  const { taxes } = variant as any
  return {
    id: variant.id,
    ean: variant.ean,
    measure_quantity: variant.measure_quantity,
    measure_unit: variant.measure_unit,
    name: variant.name,
    type: variant.type,
    sellable: variant.sellable,
    offline: variant.offline,
    weight: variant.weight,
    published: variant.published,
    packaging: variant.packaging,
    associated: variant.associated,
    cooperativeVariantId: variant.cooperative_variant_id,
    stockManagementType: variant.stock_management_type,
    taxes: taxes && {
      tva: taxes.tva,
      rpd: taxes.rpd,
      rpdUnit: taxes.rpd_unit,
    },
    isSoldAsUnit: variant.is_sold_as_unit,
    associatedSeedTreatmentManagement: variant.associated_seed_treatment_management,
    precocity: variant.precocity,
    productKind,
    productId,
    heightInM: variant.height_in_m,
    lengthInM: variant.length_in_m,
    area: variant.area,
    productConditionning: variant.product_conditionning,
    width: variant.width,
    widthUnit: variant.width_unit,
    height: variant.height,
    totalLengthM: variant.total_length_m,
    restrictedToProfiles: variant.restricted_to_profiles as Profile[] | undefined,
    associatedServices: variant.associated_services as any,
  }
}

export function isPackProduct(product: CooperativeProduct): boolean {
  return product.type === ProduitType.PACK
}

function flattenPackProduct(
  packProduct: Product | CooperativeProduct,
  productsInPack: ProduitB2AgWithoutProductsInPackApiResponse[],
): FlattenedPackProduct {
  const flattenedProduct = { ...packProduct } as any as FlattenedPackProduct

  const fieldsToFlatten: Array<{ domainField: string; apiField: string }> = [
    { domainField: 'cropProtectionProductFormulationName', apiField: 'crop_protection_product_formulation_name' },
    { domainField: 'market_authorization', apiField: 'market_authorization' },
    { domainField: 'toxicities', apiField: 'toxicities' },
    { domainField: 'associatedUses', apiField: 'associated_uses' },
    { domainField: 'active_substances', apiField: 'active_substances' },
  ]

  fieldsToFlatten.forEach(({ domainField, apiField }) => {
    ;(flattenedProduct as any)[domainField] = productsInPack.map((product) => {
      if (domainField === 'associatedUses') {
        return { id: product._id, product_name: product.name, value: (product as any)[apiField] }
      }
      return { product_name: product.name, value: (product as any)[apiField] }
    })
  })

  flattenedProduct.safetySheetUrls = productsInPack
    .filter((product) => product.safety_sheet_urls)
    .map((product) => ({
      product_name: product.name,
      value: product.safety_sheet_urls,
    }))

  return flattenedProduct
}

export const mapFromApi = (product?: ProduitCatalogueCooperativeB2AgApiResponse): CooperativeProduct => {
  if (!product) return {} as CooperativeProduct
  const {
    _id,
    slug,
    type,
    active_substances,
    chemicals_elements,
    is_mention_bio_control,
    biocide_use,
    supplier,
    supplier_logo,
    supplier_universe_page_link,
    supplier_score,
    is_supplier_highlighted,
    crop_protection_product_formulation_name,
    image_url,
    images,
    safety_sheet_urls,
    documentation,
    available_slugs,

    // special Equipment
    characteristics,

    // special Seed
    obtenteur,
    genetic_profil,
    chlortoluron_tolerance,
    resistance,
    generation,
    alternation,
    composition_mix,

    // special Nutrition Amendment
    recommendation_for_use,
    origin,
    mixed,
    contribution_mode,
    standard,
    stimulating_substance,
    is_restricted_explosives_precursor,
    type_eng,

    kind,
    market_authorization,
    name,
    is_mention_organic,
    associated_uses,
    variants = [],
    categories,
    main_category,
    toxicities,
    cooperative_review,
    is_highlighted,
    associated_products,
    exclusive,
    crop_name,
    description,
    engrais_packaging_form,
    color,
    energy_type,
    product_type,
    display_type,
    use_end_date_pm,
    marketing_end_date_pm,
    biocide_family,
    valuation,
    meta_title,
    meta_description,
  } = product
  return {
    _id: _id!,
    type,
    slug,
    active_substances,
    chemicals_elements,
    isMentionBioControl: is_mention_bio_control,
    biocideFamily: biocide_family,
    biocide_use,
    supplier,
    supplierLogo: supplier_logo,
    supplierUniversePageLink: supplier_universe_page_link,
    supplierScore: supplier_score,
    cropProtectionProductFormulationName: crop_protection_product_formulation_name,
    frontHighlight: product.front_highlight,
    imageUrl: image_url,
    medias: images,
    available_slugs,
    isRestrictedExplosivesPrecursor: !!is_restricted_explosives_precursor,
    isSupplierHighlighted: is_supplier_highlighted,
    safetySheetUrls: safety_sheet_urls || (documentation && documentation.safety_sheet_urls),
    productSheetUrls: documentation && documentation.product_sheet_urls,
    productManualUrls: documentation && documentation.product_manual_urls,
    documentation: documentation as Record<string, string[]> | undefined,
    characteristics,
    kind,
    market_authorization,
    name,
    isMentionOrganic: is_mention_organic,
    associatedUses: associated_uses,
    variants: variants.map((variant: ExtendCooperativeVariantSchema) => mapVariantFromApi(variant, kind, _id!)),
    categories,
    main_category,
    toxicities,
    cooperativeReview: cooperative_review,
    isHighlighted: is_highlighted,
    associatedProducts: associated_products?.map((associatedProduct) => ({
      _id: associatedProduct._id,
      categories: associatedProduct.categories,
      imageUrl: associatedProduct.image_url ?? '',
      name: associatedProduct.name,
      supplier: associatedProduct.supplier,
      kind: associatedProduct.kind,
      sellable: associatedProduct.sellable,
    })),
    exclusive,
    cropName: crop_name,
    obtenteur,
    genetic_profil: genetic_profil,
    chlortoluron_tolerance,
    resistance,
    generation,
    alternation,
    compositionMix: composition_mix,
    description,
    engraisPackagingForm: engrais_packaging_form,
    recommendationForUse: recommendation_for_use,
    origin,
    mixed,
    contributionMode: contribution_mode,
    standard,
    stimulatingSubstance: stimulating_substance,
    color,
    energyType: energy_type,
    productType: product_type,
    displayType: display_type,
    typeEng: type_eng,
    useEndDatePm: use_end_date_pm,
    marketingEndDatePm: marketing_end_date_pm,
    valuation,
    meta_title,
    meta_description,
    // attribut family encore utile pour quota
    family: main_category?.[0]?.label || '',
  }
}

export const mapProductFromApiAndFlattenPackProduct = (
  responseProduct?: ProduitCatalogueCooperativeB2AgApiResponse,
): CooperativeProduct => {
  const product = mapFromApi(responseProduct)

  if (responseProduct) {
    return isPackProduct(product) ? flattenPackProduct(product, responseProduct.products_in_pack!) : product
  }

  return product
}

export const mapMeasureUnit = (str: string): string => (str === 'l' ? 'L' : str)

export const mapOfferFromOfferApi = (offer: OfferSchema): OfferForQuota => ({
  offerId: offer.offer_id,
  validityEndDate: offer.validity_end_date ? new Date(offer.validity_end_date) : undefined,
  shippingStartDate: offer.shipping_start_date ? new Date(offer.shipping_start_date) : undefined,
  shippingEndDate: offer.shipping_end_date ? new Date(offer.shipping_end_date) : undefined,
  shippingMethodList: offer.shipping_method_list?.map(mapShippingWayFromApi),
  segmentList: offer.segment_list,
})
